import {recordRumError} from '../../util/datadog/datadogRum';
import {envIsProd} from './../../config';

/**
 *  High level notes on error reporting:
 * 1. Datadog RUM reports console errors on its own, so these helpers exist for the
 *    errors we deliberately keep out of the console in production
 * 2. We don't want to spam RUM with handled exceptions, but we do want them in the
 *    console for debugging while watching user sessions
 * 3. In a dev environment we want high visibility on errors (even handled ones?), so
 *    console.warn in prod, and console.error elsewhere
 * 4. Outside prod the console line is the report: RUM picks console errors up itself,
 *    so reporting again here would double-count them
 * 5. RUM context is flat, so the nested extra/tags split collapses into one object
 */

type ErrorContext = {
  extra?: {[key: string]: unknown};
  tags?: {[key: string]: string};
};

/**
 * Reports a handled error, which by definition doesn't reach the console.
 *
 * @param msg - a message describing what was handled
 * @param context - optional extras/tags, flattened into the RUM context
 */
export const logHandledError = (msg: string, context?: ErrorContext): void => {
  if (envIsProd) {
    recordRumError(msg, toRumContext(context));
  }
};

/**
 * Reports an error and logs it to the console.
 *
 * @param err - the error to report
 * @param context - optional extras/tags, flattened into the RUM context
 */
export const logError = (err: unknown, context?: ErrorContext): void => {
  if (envIsProd) {
    console.warn(err, context); // warn in prod so we see it in the console messages
    recordRumError(err, toRumContext(context));
    return;
  }

  console.error(err, context); // error in dev
};

function toRumContext(
  context: ErrorContext | undefined
): {[key: string]: unknown} | undefined {
  if (context == null) {
    return undefined;
  }
  return {...context.extra, ...context.tags};
}
